import type { Flow } from "@learnengine/shared-types";

export interface AudioCue {
  id: string;
  startSeconds: number;
  endSeconds: number;
}

export interface AudioSnapshot {
  isPlaying: boolean;
  currentTimeSeconds: number;
  activeCueId: string | null;
}

/**
 * AudioController (Phase 2) is a mocked audio layer.
 * It does not generate audio; it simply simulates cue timing and exposes state
 * so the runtime can validate sync behavior deterministically.
 */
export class AudioController {
  private flow: Flow | null = null;
  private cues: AudioCue[] = [];
  private isPlaying = false;
  private currentTimeSeconds = 0;
  private activeCueId: string | null = null;

  loadFlow(flow: Flow) {
    this.flow = flow;
    this.isPlaying = false;
    this.currentTimeSeconds = 0;

    // Build a simple cue list: one cue per node if node.audio exists.
    // Cue duration defaults to node duration.
    let accumulated = 0;
    this.cues = flow.nodes
      .map((node) => {
        const audio = node.audio;
        if (!audio) {
          accumulated += node.durationSeconds;
          return null;
        }
        const offset = audio.offsetSeconds ?? 0;
        const startSeconds = accumulated + offset;
        const endSeconds = accumulated + node.durationSeconds;
        accumulated += node.durationSeconds;
        return {
          id: audio.id,
          startSeconds,
          endSeconds,
        } satisfies AudioCue;
      })
      .filter((c): c is AudioCue => Boolean(c));

    this.recomputeActiveCue();
  }

  play() {
    if (!this.flow) return;
    this.isPlaying = true;
  }

  pause() {
    this.isPlaying = false;
  }

  seek(timeSeconds: number) {
    this.currentTimeSeconds = Math.max(0, timeSeconds);
    this.recomputeActiveCue();
  }

  /**
   * Update should be called on every timeline tick.
   */
  update(timeSeconds: number) {
    this.currentTimeSeconds = Math.max(0, timeSeconds);
    this.recomputeActiveCue();
  }

  snapshot(): AudioSnapshot {
    return {
      isPlaying: this.isPlaying,
      currentTimeSeconds: this.currentTimeSeconds,
      activeCueId: this.activeCueId,
    };
  }

  private recomputeActiveCue() {
    const t = this.currentTimeSeconds;
    const cue = this.cues.find((c) => t >= c.startSeconds && t < c.endSeconds) ?? null;
    this.activeCueId = cue?.id ?? null;
  }
}
